import * as React from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { getInitials } from '@/lib/utils/user-helpers'
import { User, Workspace, Project, Task, Document, Message } from '@/types/nodes'
import { cn } from '@/lib/utils/cn'
import { type AvatarTextSize, getAvatarTextSizeClasses } from '@/lib/utils/text-size-utils'

/**
 * Size variants for avatar display
 */
export type AvatarSize = AvatarTextSize

/**
 * Supported node types for avatar display
 */
export type AvatarNode = User | Workspace | Project | Task | Document | Message

/**
 * Maps size variants to avatar dimensions
 */
const sizeMap: Record<AvatarSize, string> = {
  xs: 'h-4 w-4',
  sm: 'h-6 w-6',
  md: 'h-8 w-8',
  lg: 'h-10 w-10',
  xl: 'h-16 w-16',
  xxl: 'h-24 w-24',
}

/**
 * Gets the avatar source URL for different node types
 */
function getAvatarSource(node: AvatarNode): string | undefined {
  if (!node) return undefined

  if ('avatar' in node) {
    // User node
    return node.avatar
  }

  if ('logo' in node) {
    // Workspace node
    return node.logo
  }

  // For other node types (Project, Task, Document, Message), no avatar source
  return undefined
}

/**
 * Gets the display name for different node types
 */
function getDisplayName(node: AvatarNode): string {
  if (!node) return 'Unknown'

  if ('name' in node) {
    return node.name
  }

  return 'Unknown'
}

/**
 * Gets the appropriate fallback icon for different node types
 */
function getFallbackIcon(node: AvatarNode) {
  if (!node) return '??'

  // For now, we'll use initials for all node types
  // This can be extended later to use specific icons for different node types
  return getInitials(getDisplayName(node))
}

/**
 * Props for the NodeAvatar component
 */
export interface NodeAvatarProps {
  /**
   * The node to display avatar for
   */
  node: AvatarNode
  /**
   * Size variant for the avatar (defaults to 'md')
   */
  size?: AvatarSize
  /**
   * Optional custom className for the avatar container
   */
  className?: string
  /**
   * Optional alt text (defaults to node name/title)
   */
  alt?: string
  /**
   * Whether to show a tooltip with the node name on hover
   */
  showTooltip?: boolean
  /**
   * Optional click handler
   */
  onClick?: () => void
  /**
   * Optional custom fallback content
   */
  fallback?: React.ReactNode
}

/**
 * A flexible and reusable component for displaying node avatars
 * Supports different node types (User, Workspace, Project, Task, Document, Message)
 * with appropriate fallbacks and size variants
 *
 * @example
 * ```tsx
 * // Basic usage with user
 * <NodeAvatar node={user} />
 *
 * // With workspace and custom size
 * <NodeAvatar node={workspace} size="lg" />
 *
 * // With custom styling and click handler
 * <NodeAvatar
 *   node={project}
 *   size="xl"
 *   className="border-2 border-primary"
 *   onClick={() => navigate(`/projects/${project.id}`)}
 * />
 *
 * // With custom fallback
 * <NodeAvatar
 *   node={task}
 *   fallback={<CustomIcon />}
 * />
 * ```
 */
export function NodeAvatar({
  node,
  size = 'md',
  className,
  alt,
  showTooltip = false,
  onClick,
  fallback,
}: NodeAvatarProps) {
  if (!node) {
    return (
      <Avatar className={cn(sizeMap[size], className)}>
        <AvatarFallback className={cn(getAvatarTextSizeClasses(size))}>??</AvatarFallback>
      </Avatar>
    )
  }

  const avatarSource = getAvatarSource(node)
  const displayName = getDisplayName(node)
  const defaultAlt = alt || displayName

  const avatarComponent = (
    <Avatar className={cn(sizeMap[size], className)}>
      <AvatarImage src={avatarSource} alt={defaultAlt} />
      <AvatarFallback className={cn(getAvatarTextSizeClasses(size))}>
        {fallback || getFallbackIcon(node)}
      </AvatarFallback>
    </Avatar>
  )

  if (showTooltip) {
    return (
      <div title={displayName} className={cn(onClick && 'cursor-pointer')}>
        {avatarComponent}
      </div>
    )
  }

  return avatarComponent
}
